import { CoreButtonView, CoreTextView, TCoreButtonStyleProps, type TCoreButtonViewProps } from '@core/widgets';
import { ConditionalRender } from '@libs/core';
import { TButtonData, TViewCommonOption, TWidget } from '@libs/types';
import React, { ElementType, MouseEvent, MouseEventHandler } from 'react';
import { getSemanticColors, memoByPalette, SemanticColor, TSemanticColors } from '../../theme/colors';
import { TButtonOption } from '../../types/options';
import { TextStyle } from '../../types/styles';
import { VisualView } from '../Image/VisualView';
import { isV2 } from '../../utils/widgetVersion';
import { getIconHierarchy } from './ButtonCircleView';

type TButtonRowStyleProps = Pick<TButtonOption, 'margin' | 'iconName' | 'iconVariant' | 'size' | 'alignment' | 'hierarchy' | 'iconHierarchy'>;

export type TButtonRowViewProps = {
    widget?: TWidget<TButtonData, TButtonOption>;
    uid?: string;
    label?: string;
    as?: ElementType;
    /**
     * Called when the button is clicked. Optional in the standalone (non-widget) form
     * when the element is used as a non-interactive label (e.g. `as="div"`).
     */
    onClick?: MouseEventHandler;
    children?: React.ReactNode;
    /** Design version of the widget it renders for (`TWidget.version`): picks semantic colors and icons. */
    version: number | undefined;
} & Pick<TViewCommonOption, 'isDisabled' | 'isHide' | 'parentStyleNames'> &
    TButtonRowStyleProps;

type THierarchy = NonNullable<TButtonOption['hierarchy']>;

const buildHierarchyColorMap = (colors: TSemanticColors): Record<THierarchy, Pick<TCoreButtonStyleProps, 'main' | 'disabled' | 'pressed' | 'hovered'>> => ({
    highlighted: {
        main: colors[SemanticColor.TextEmphasised],
        hovered: {
            main: colors[SemanticColor.TextEmphasised],
        },
        pressed: {
            main: colors[SemanticColor.TextEmphasised],
        },
        disabled: {
            main: colors[SemanticColor.TextEmphasisedDisabled],
        },
    },
    primary: {
        main: colors[SemanticColor.TextEmphasised],
        hovered: {
            main: colors[SemanticColor.TextEmphasised],
        },
        pressed: {
            main: colors[SemanticColor.TextEmphasised],
        },
        disabled: {
            main: colors[SemanticColor.TextEmphasisedDisabled],
        },
    },
    destructive: {
        main: colors[SemanticColor.TextError],
        hovered: {
            main: colors[SemanticColor.TextError],
        },
        pressed: {
            main: colors[SemanticColor.TextError],
        },
        disabled: {
            main: colors[SemanticColor.TextErrorDisabled],
        },
    },
    secondary: {
        main: colors[SemanticColor.TextEmphasised],
        hovered: {
            main: colors[SemanticColor.TextEmphasised],
        },
        pressed: {
            main: colors[SemanticColor.TextEmphasised],
        },
        disabled: {
            main: colors[SemanticColor.TextEmphasisedDisabled],
        },
    },
    tertiary: {
        main: colors[SemanticColor.TextEmphasised],
        hovered: {
            main: colors[SemanticColor.TextEmphasised],
        },
        pressed: {
            main: colors[SemanticColor.TextEmphasised],
        },
        disabled: {
            main: colors[SemanticColor.TextEmphasisedDisabled],
        },
    },
    tertiaryGlass: {},
});
const getHierarchyColorMap = memoByPalette(buildHierarchyColorMap);

type TRowFill = Record<'main' | 'hovered' | 'pressed' | 'disabled', SemanticColor>;

/** Background of every non-destructive hierarchy: v1 keeps the ghost fill, v2 paints the tertiary fill. */
const ROW_FILL_V1: TRowFill = {
    main: SemanticColor.FillGhost,
    hovered: SemanticColor.FillGhostHovered,
    pressed: SemanticColor.FillGhostPressed,
    disabled: SemanticColor.FillGhostDisabled,
};
const ROW_FILL_V2: TRowFill = {
    main: SemanticColor.FillTertiary,
    hovered: SemanticColor.FillTertiaryHovered,
    pressed: SemanticColor.FillTertiaryPressed,
    disabled: SemanticColor.FillTertiaryDisabled,
};

const buildHierarchyBgMap = (
    colors: TSemanticColors,
    rowFill: TRowFill,
): Record<THierarchy, Pick<TCoreButtonStyleProps, 'main' | 'disabled' | 'pressed' | 'hovered'>> => {
    const fill = {
        main: colors[rowFill.main],
        hovered: { main: colors[rowFill.hovered] },
        pressed: { main: colors[rowFill.pressed] },
        disabled: { main: colors[rowFill.disabled] },
    };
    return {
        highlighted: fill,
        primary: fill,
        destructive: {
            main: colors[SemanticColor.FillDestructive],
            hovered: {
                main: colors[SemanticColor.FillDestructiveHovered],
            },
            pressed: {
                main: colors[SemanticColor.FillDestructivePressed],
            },
            disabled: {
                main: colors[SemanticColor.FillDestructiveDisabled],
            },
        },
        secondary: fill,
        tertiary: fill,
        tertiaryGlass: {},
    };
};
const getHierarchyBgMapV1 = memoByPalette((colors) => buildHierarchyBgMap(colors, ROW_FILL_V1));
const getHierarchyBgMapV2 = memoByPalette((colors) => buildHierarchyBgMap(colors, ROW_FILL_V2));

/** v2 label of a primary row without an icon (the "Left" / "Center" alignments of the design). */
const getHighlightedLabelColors = memoByPalette((colors): Pick<TCoreButtonStyleProps, 'main' | 'disabled' | 'pressed' | 'hovered'> => ({
    main: colors[SemanticColor.TextHighlighted],
    hovered: {
        main: colors[SemanticColor.TextHighlighted],
    },
    pressed: {
        main: colors[SemanticColor.TextHighlighted],
    },
    disabled: {
        main: colors[SemanticColor.TextHighlightedDisabled],
    },
}));

const SIZE_PADDING_MAP: Record<string, Pick<TCoreButtonStyleProps, 'padding'>> = {
    large: {
        padding: { top: 24, right: 16, bottom: 24, left: 24 },
    },
    largeWithIcon: {
        padding: { top: 16, right: 24, bottom: 16, left: 16 },
    },
    small: {
        padding: { top: 16, right: 16, bottom: 16, left: 16 },
    },
    smallWithIcon: {
        padding: { top: 8, right: 24, bottom: 8, left: 16 },
    },
};

/**
 * Composable building block for the ButtonRow widget.
 * Accepts pre-resolved behavior props (isDisabled, isHide, onClick) from the parent,
 * making it suitable for scenarios where behavior state is already managed one level up.
 * Delegates theme resolution and all rendering to CoreButtonView.
 */
export const ButtonRowView = ({
    uid,
    widget,
    label,
    isDisabled,
    isHide,
    onClick,
    as = 'button',
    parentStyleNames,
    iconName,
    iconVariant = 'simple',
    size,
    alignment,
    hierarchy = 'primary' as THierarchy,
    iconHierarchy,
    margin,
    version,
}: TButtonRowViewProps) => {
    const semanticColors = getSemanticColors(version);
    const isV2Design = isV2({ version });
    const resolvedParentStyleNames = parentStyleNames ?? [];
    // Figma ButtonRow (Style Guide 3.0, node 26016-316646): v2 primary label is highlighted only in the variants without an icon (Left / Center), with an icon it stays emphasised.
    const labelColors =
        isV2Design && hierarchy === 'primary' && !iconName ? getHighlightedLabelColors(semanticColors) : getHierarchyColorMap(semanticColors)[hierarchy];

    const hasSizeKey = size === 'large' ? (iconName ? 'largeWithIcon' : 'large') : iconName ? 'smallWithIcon' : 'small';

    const coreButtonStyleProps: TCoreButtonStyleProps = {
        ...(isV2Design ? getHierarchyBgMapV2 : getHierarchyBgMapV1)(semanticColors)[hierarchy],
        ...SIZE_PADDING_MAP[hasSizeKey],
        fillContainer: true,
        margin: margin ?? {},
        borderRadius: 0,
        fontStyle: { fontSize: 17, lineHeight: 24 },
        cursorDisabled: 'default',
        textAlign: (['center', 'start', 'end'] as const).find((v) => v === alignment) ?? 'start',
        gap: alignment === 'center' ? 8 : 16,
    };

    return (
        <CoreButtonView
            uid={uid}
            widget={widget as TCoreButtonViewProps['widget']}
            text={label}
            isDisabled={isDisabled}
            isHide={isHide}
            styleProps={coreButtonStyleProps}
            parentStyleNames={resolvedParentStyleNames}
            as={as}
            styleName="rowButton"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
                onClick?.(e);
            }}
        >
            <ConditionalRender condition={!!iconName}>
                <VisualView
                    version={version}
                    parentStyleNames={resolvedParentStyleNames}
                    styleName="rowButtonIcon"
                    isDisabled={isDisabled}
                    iconName={iconName}
                    iconVariant={iconVariant}
                    iconHierarchy={iconHierarchy ? getIconHierarchy(iconHierarchy as string) : hierarchy === 'destructive' ? 'destructive' : 'emphasised'}
                />
            </ConditionalRender>
            <CoreTextView
                text={label}
                isDisabled={isDisabled}
                as="span"
                parentStyleNames={resolvedParentStyleNames}
                styleName="rowButtonText"
                styleProps={{
                    ...TextStyle.main,
                    ...labelColors,
                }}
            />
        </CoreButtonView>
    );
};
